/**
 * Projeção nativa (grade fixa geoestacionária) do GOES-19 e extensão do
 * setor NSA do STAR. As imagens do STAR são exibidas sem nenhuma
 * reprojeção; os vetores (lon/lat) é que são projetados para cá.
 *
 * Unidades do mapa: metros = ângulo de varredura (rad) × altura do satélite.
 */
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import { get as getProjection, transform, transformExtent } from 'ol/proj';
import type { Extent } from 'ol/extent';
import georef from '../config/georef.json';

export const GOES_PROJ = 'GOES19';

/** Altura do satélite acima do elipsoide (m). */
export const SAT_HEIGHT = georef.h;

const PROJ4_DEF =
  `+proj=geos +h=${georef.h} +lon_0=${georef.lon0} +sweep=${georef.sweep} ` +
  `+ellps=${georef.ellps} +units=m +no_defs +type=crs`;

/** Ângulo de varredura do limbo da Terra (~0.1519 rad), usado como limite do disco. */
const DISK_RAD = 0.1519;

/** Extensão do disco completo, em metros da projeção. */
export const DISK_EXTENT: Extent = [
  -DISK_RAD * SAT_HEIGHT,
  -DISK_RAD * SAT_HEIGHT,
  DISK_RAD * SAT_HEIGHT,
  DISK_RAD * SAT_HEIGHT,
];

/** Extensão (bordas externas) da imagem do setor NSA, em metros da projeção. */
export const NSA_EXTENT: Extent = [
  georef.xMin * SAT_HEIGHT,
  georef.yMin * SAT_HEIGHT,
  georef.xMax * SAT_HEIGHT,
  georef.yMax * SAT_HEIGHT,
];

/** Caixa aproximada da Amazônia Legal (lon/lat), para enquadramento inicial. */
export const AMAZONIA_LEGAL_BBOX_LONLAT: Extent = [-74.0, -18.1, -43.9, 5.3];

let registered = false;

/** Registra a projeção do GOES-19 no OpenLayers (idempotente). */
export function registerGoesProjection(): void {
  if (registered) return;
  proj4.defs(GOES_PROJ, PROJ4_DEF);
  register(proj4);
  const proj = getProjection(GOES_PROJ);
  if (!proj) throw new Error('Falha ao registrar a projeção do GOES-19');
  proj.setExtent(DISK_EXTENT);
  registered = true;
}

/** lon/lat (graus) → coordenada do mapa. Pontos além do limbo retornam Infinity. */
export function lonLatToMap(lon: number, lat: number): [number, number] {
  registerGoesProjection();
  return transform([lon, lat], 'EPSG:4326', GOES_PROJ) as [number, number];
}

/** Coordenada do mapa → lon/lat (graus). Fora do disco retorna Infinity/NaN. */
export function mapToLonLat(x: number, y: number): [number, number] {
  registerGoesProjection();
  return transform([x, y], GOES_PROJ, 'EPSG:4326') as [number, number];
}

/** Extensão inicial do mapa: a Amazônia Legal projetada. */
export function amazoniaLegalExtent(): Extent {
  registerGoesProjection();
  return transformExtent(AMAZONIA_LEGAL_BBOX_LONLAT, 'EPSG:4326', GOES_PROJ, 16);
}

export const isGeorefCalibrated = georef.calibrated;
